import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { FileUp, RefreshCw } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  MdTabs,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  ProductPicker,
  SearchInput,
  Select,
  Toggle,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import { useHasAnyPermission, usePermission } from '@/shell/module-system'
import { DOC_REQUEST_CREATE_ROLES } from '@/shell/services/regulatory/api/doc-requests'
import { RequestDocsDrawer } from '@/shell/services/regulatory/components/RequestDocsDrawer'
import { useDocumentCoverage, useExpiringDocuments } from '../api/queries'
import type { CoverageRow, ExpiringDocument } from '../api/types'
import { DocumentTypesPanel } from '../components/DocumentTypesPanel'
import { OnboardingList } from '../components/OnboardingList'
import { VendorRequestsList } from '../components/VendorRequestsList'
import { ExpiryChip } from '../components/ExpiryChip'

type Tab = 'onboarding' | 'vendor-requests' | 'coverage' | 'expiring' | 'types'
const PAGE_SIZE = 100

/**
 * Products — regulatory document coverage across the catalog, documents
 * expiring soon, and (admin) the document-type list. Each product's
 * documents live on /products/:id/documents.
 */
export function ProductsPage() {
  const isAdmin = usePermission('admin')
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string }
  const initial: Tab =
    search.tab === 'expiring' || search.tab === 'coverage' || search.tab === 'vendor-requests' || (search.tab === 'types' && isAdmin)
      ? (search.tab as Tab)
      : 'onboarding'
  const [tab, setTab] = useState<Tab>(initial)

  const openProduct = (id: number) => navigate({ to: '/products/$tmplId/documents', params: { tmplId: String(id) } })

  const openIntake = (id: string) => navigate({ to: '/products/onboarding/$intakeId', params: { intakeId: id } })

  const tabs = [
    { id: 'onboarding' as const, label: 'New products' },
    { id: 'vendor-requests' as const, label: 'Vendor requests' },
    { id: 'coverage' as const, label: 'Document coverage' },
    { id: 'expiring' as const, label: 'Expiring' },
    ...(isAdmin ? [{ id: 'types' as const, label: 'Document types' }] : []),
  ]

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="Products"
        description="New products are set up, reviewed and signed off here before they reach Odoo. Every product's regulatory documents live here; Odoo links to them."
      >
        <div className="ml-auto w-80">
          <ProductPicker
            value={null}
            onChange={(next) => next && openProduct(next[0])}
            placeholder="Open a product's documents…"
            width={360}
            searchEndpoint="/api/product-documents/search/products"
          />
        </div>
      </PageHeaderBand>
      <div className="border-b border-border-subtle bg-surface px-4">
        <MdTabs tabs={tabs} value={tab} onChange={setTab} />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {tab === 'onboarding' ? <OnboardingList onOpen={openIntake} /> : null}
        {tab === 'vendor-requests' ? (
          <VendorRequestsList onOpen={(id) => navigate({ to: '/products/vendor-requests/$requestId', params: { requestId: id } })} />
        ) : null}
        {tab === 'coverage' ? <CoverageTab onOpen={openProduct} /> : null}
        {tab === 'expiring' ? <ExpiringTab onOpen={openProduct} /> : null}
        {tab === 'types' && isAdmin ? <DocumentTypesPanel /> : null}
      </div>
    </div>
  )
}

function CoverageTab({ onOpen }: { onOpen: (id: number) => void }) {
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  const [onlyMissing, setOnlyMissing] = useState(true)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const id = setTimeout(() => {
      setQ(text.trim())
      setOffset(0)
    }, 250)
    return () => clearTimeout(id)
  }, [text])

  const query = useMemo(() => ({ q: q || undefined, onlyMissing, offset, limit: PAGE_SIZE }), [q, onlyMissing, offset])
  const { data, isLoading, isError, error, refetch, isFetching } = useDocumentCoverage(query)

  const columns = useMemo<ColumnDef<CoverageRow, unknown>[]>(
    () => [
      { id: 'code', header: 'Code', cell: (ctx) => ctx.row.original.default_code ?? '—' },
      { id: 'name', header: 'Product', cell: (ctx) => ctx.row.original.name },
      { id: 'docs', header: 'Documents', cell: (ctx) => ctx.row.original.document_count },
      {
        id: 'missing',
        header: 'Missing required',
        cell: (ctx) =>
          ctx.row.original.missing_required_labels.length ? (
            <div className="flex flex-wrap gap-1">
              {ctx.row.original.missing_required_labels.map((label) => (
                <Chip key={label} variant="warning">
                  {label}
                </Chip>
              ))}
            </div>
          ) : (
            <Chip variant="success" dot>
              Complete
            </Chip>
          ),
      },
    ],
    [],
  )

  const total = data?.total ?? 0
  return (
    <>
      <Toolbar>
        <SearchInput
          containerClassName="flex-1 max-w-none"
          placeholder="Search product name or code…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Search products"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <Toggle
            label="Only products missing required documents"
            checked={onlyMissing}
            onChange={(e) => {
              setOnlyMissing(e.target.checked)
              setOffset(0)
            }}
          />
        </ToolbarLeft>
        <ToolbarRight>
          <ToolbarCount>
            {total.toLocaleString()} {onlyMissing ? 'products with gaps' : 'products'}
          </ToolbarCount>
          <Button
            variant="ghost"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button variant="ghost" size="sm" disabled={!data?.truncated} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh"
            loading={isFetching && !isLoading}
            onClick={() => void refetch()}
          >
            <RefreshCw size={14} />
          </Button>
        </ToolbarRight>
      </Toolbar>
      <div className="flex min-h-0 flex-1 flex-col bg-canvas p-4">
        <DataTable
          data={data?.items ?? []}
          columns={columns}
          loading={isLoading}
          error={isError ? error : undefined}
          onRetry={() => void refetch()}
          errorSubject="product document coverage"
          getRowId={(r) => String(r.product_tmpl_id)}
          onRowClick={(r) => onOpen(r.product_tmpl_id)}
          moduleId="products-coverage"
        />
      </div>
    </>
  )
}

function ExpiringTab({ onOpen }: { onOpen: (id: number) => void }) {
  const [days, setDays] = useState(60)
  const { data, isLoading, isError, error, refetch, isFetching } = useExpiringDocuments(days)
  const navigate = useNavigate()
  const canRequest = useHasAnyPermission(DOC_REQUEST_CREATE_ROLES)
  const [requestFor, setRequestFor] = useState<ExpiringDocument | null>(null)
  const items = data?.items ?? []
  const expired = items.filter((i) => i.expired).length

  const columns = useMemo<ColumnDef<ExpiringDocument, unknown>[]>(
    () => [
      { id: 'expires', header: 'Expires', cell: (ctx) => <ExpiryChip expiresOn={ctx.row.original.expires_on} /> },
      { id: 'type', header: 'Document type', cell: (ctx) => ctx.row.original.doc_type_label },
      {
        id: 'product',
        header: 'Product',
        cell: (ctx) =>
          [ctx.row.original.product_default_code, ctx.row.original.product_name].filter(Boolean).join(' · ') || '—',
      },
      { id: 'version', header: 'Version', cell: (ctx) => ctx.row.original.version_label ?? '—' },
      ...(canRequest ? [{
        id: 'request',
        header: '',
        cell: (ctx: { row: { original: ExpiringDocument } }) => (
          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setRequestFor(ctx.row.original) }}>
            <FileUp size={14} /> Request from vendor
          </Button>
        ),
      } satisfies ColumnDef<ExpiringDocument, unknown>] : []),
    ],
    [canRequest],
  )

  return (
    <>
      <RequestDocsDrawer
        open={requestFor != null}
        onOpenChange={(o) => { if (!o) setRequestFor(null) }}
        onOpenCard={(id) => void navigate({ to: '/regulatory/vendor/$requestId', params: { requestId: id } })}
        initialProducts={requestFor?.product_tmpl_id != null ? [{
          product_tmpl_id: requestFor.product_tmpl_id, name: requestFor.product_name ?? `Product ${requestFor.product_tmpl_id}`,
          default_code: requestFor.product_default_code, doc_types: [requestFor.doc_type_key],
        }] : []}
      />
      <Toolbar>
        <ToolbarLeft>
          <Select aria-label="Window" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="0">Already expired</option>
            <option value="30">Expired or within 30 days</option>
            <option value="60">Expired or within 60 days</option>
            <option value="90">Expired or within 90 days</option>
            <option value="180">Expired or within 180 days</option>
          </Select>
        </ToolbarLeft>
        <ToolbarRight>
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Expired" value={expired.toLocaleString()} />
            <MetricCard variant="inline" label="Expiring" value={(items.length - expired).toLocaleString()} />
          </MetricStrip>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh"
            loading={isFetching && !isLoading}
            onClick={() => void refetch()}
          >
            <RefreshCw size={14} />
          </Button>
        </ToolbarRight>
      </Toolbar>
      <div className="flex min-h-0 flex-1 flex-col bg-canvas p-4">
        <DataTable
          data={items}
          columns={columns}
          loading={isLoading}
          error={isError ? error : undefined}
          onRetry={() => void refetch()}
          errorSubject="expiring documents"
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => r.product_tmpl_id != null && onOpen(r.product_tmpl_id)}
          criticalRowPredicate={(r) => r.expired}
          moduleId="products-expiring"
        />
      </div>
    </>
  )
}
